import { useState } from 'react';
import type { ReactNode } from 'react';

import Button from '@classify/react-core-components/Button';
import Typography from '@classify/react-core-components/Typography';

import { getOcClient } from '../../../../../configuration';

interface Props {
  children: ReactNode;
  environment: number;
  userId: number;
  projectId: number;
  translations: {
    forbiddenError: string;
    genericError: string;
  };
}

interface ApiError extends Error {
  statusCode?: number;
}

export default function RootMeButton({
  children,
  environment,
  userId,
  projectId,
  translations,
}: Props) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [ltiAuthInfos, setLtiAuthInfos] = useState<{ [key: string]: string }>();

  const handleClickToRedirect = async () => {
    setIsRedirecting(true);
    try {
      const { body } = await getOcClient()
        .helpers.user.users.id(userId)
        .projects.id(projectId)
        .securityAudit.id(environment)
        .generateLtiSignature.get();

      setLtiAuthInfos(body);
    } catch (error) {
      const { statusCode } = error as ApiError;

      if (statusCode === 403) {
        setErrorMessage(translations.forbiddenError);
      } else {
        setErrorMessage(translations.genericError);
      }
      setIsRedirecting(false);
    }
  };

  const handleFormReady = (form: HTMLFormElement) => {
    if (!form) {
      return;
    }

    form.submit();
    setIsRedirecting(false);
  };

  return (
    <>
      <Button isLoading={isRedirecting} variant="primary" onClick={handleClickToRedirect}>
        {children}
      </Button>

      {ltiAuthInfos && (
        <form
          data-testid={`rootme-form-${projectId}`}
          action="https://www.root-me.org/?action=api_lti"
          method="post"
          target="_blank"
          ref={handleFormReady}
        >
          {Object.entries(ltiAuthInfos).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}
        </form>
      )}

      {errorMessage && (
        <Typography sx={{ display: 'block', marginTop: 2 }} variant="body">
          {errorMessage}
        </Typography>
      )}
    </>
  );
}
